/**
 * Date format constants for consistent usage across the application.
 * These follow moment.js format patterns.
 */
export const DATE_FORMATS = {
    /** Date only format: MM/DD/YYYY (e.g., "06/15/2025") */
    DATE_ONLY: 'MM/DD/YYYY',
    /** Time only format: hh:mm A (e.g., "02:30 PM") */
    TIME_ONLY: 'hh:mm A',
    /** Date and time format: MM/DD/YYYY hh:mm A (e.g., "06/15/2025 02:30 PM") */
    DATE_TIME: 'MM/DD/YYYY hh:mm A',
    /** ISO date format for API requests: YYYY-MM-DD (e.g., "2025-06-15") */
    API_DATE: 'YYYY-MM-DD'
};

/**
 * Returns the appropriate moment.js format string based on time display settings.
 * Used by DateInput and DatePicker components for consistent date/time formatting.
 *
 * @param {boolean} showTimeSelectOnly - Whether to show only time (no date)
 * @param {boolean} showTimeSelect - Whether to show time along with date
 * @returns {string} Format string for moment.js
 *
 * @example
 * // Date only
 * getDisplayFormat(false, false) // Returns 'MM/DD/YYYY'
 *
 * @example
 * // Time only
 * getDisplayFormat(true, false) // Returns 'hh:mm A'
 *
 * @example
 * // Date and time
 * getDisplayFormat(false, true) // Returns 'MM/DD/YYYY hh:mm A'
 */
export const getDisplayFormat = (showTimeSelectOnly, showTimeSelect) => {
    if (showTimeSelectOnly) {
        return DATE_FORMATS.TIME_ONLY;
    } else if (showTimeSelect) {
        return DATE_FORMATS.DATE_TIME;
    }
    return DATE_FORMATS.DATE_ONLY;
};

/**
 * Whether a date-picker question represents an absolute instant (date + time) —
 * the only shape where a researcher's profile timezone applies. Date-only and
 * time-only answers are TZ-agnostic: shifting them by an offset would change
 * the calendar day or clock hour the participant picked.
 *
 * @param {boolean} showTimeSelectOnly - Question is time-only
 * @param {boolean} showTimeSelect - Question includes a time component
 * @returns {boolean} true when the value is a TZ-aware datetime
 */
export const isDateTimeQuestion = (showTimeSelectOnly, showTimeSelect) =>
    Boolean(showTimeSelect && !showTimeSelectOnly);
